import { useCallback, useMemo, useState } from 'react';

import { ErrorFallback, type WidgetErrorProps } from '@/shared/ui/ErrorBoundary';

import { POD_SERIES_COLORS } from '@/features/monitoring/api/constants';
import type { GpuMemoryUsedMetric } from '@/features/monitoring/api/types';
import { useGpuMemoryUsed } from '@/features/monitoring/model';

import { MetricCard } from '@/widgets/monitoring/MetricCard';
import { PodMetricDetailModal } from '@/widgets/monitoring/PodMetricDetailModal';

import styles from './GpuMemoryMetric.module.css';

type GpuMemoryMetricContentProps = {
  data?: GpuMemoryUsedMetric[];
  isLoading?: boolean;
};

type GpuMemoryMetricSeries = {
  pod: string;
  data: { timestamp: number; value: number }[];
  color: string;
};

type GpuMemoryMetricViewModel = {
  currentUsage: number;
  gpuInstanceCount: number;
  gpuSeries: GpuMemoryMetricSeries[];
};

const MIB_TO_GB = 1 / 1024;
const convertMibToGb = (value: number) => value * MIB_TO_GB;

const EMPTY_GPU_MEMORY_VIEW_MODEL: GpuMemoryMetricViewModel = {
  currentUsage: 0,
  gpuInstanceCount: 0,
  gpuSeries: [],
};

const getGpuSuffix = (metric: GpuMemoryUsedMetric) => {
  const instance = metric.gpuInstanceId ? `MIG ${metric.gpuInstanceId}` : `GPU ${metric.gpu}`;
  return `${metric.hostname}/${metric.device}/${instance}`;
};

const getSeriesName = (metric: GpuMemoryUsedMetric, isDuplicate: boolean) => {
  if (!isDuplicate) {
    return metric.displayName;
  }

  return `${metric.displayName} · ${getGpuSuffix(metric)}`;
};

const GpuMemoryMetricView = ({ data: gpuMemoryData, isLoading = false }: GpuMemoryMetricContentProps) => {
  const [gpuMemoryModalOpen, setGpuMemoryModalOpen] = useState(false);

  const handleDetailClick = useCallback(() => {
    setGpuMemoryModalOpen((prev) => !prev);
  }, []);

  const gpuMemoryViewModel = useMemo(() => {
    const metrics = gpuMemoryData ?? [];

    if (metrics.length === 0) {
      return EMPTY_GPU_MEMORY_VIEW_MODEL;
    }

    const displayNameCounts = new Map<string, number>();
    let currentUsage = 0;

    for (const metric of metrics) {
      displayNameCounts.set(metric.displayName, (displayNameCounts.get(metric.displayName) ?? 0) + 1);
      currentUsage += convertMibToGb(metric.last);
    }

    const shallowCopy = [...metrics].slice();
    const sorted = shallowCopy.sort((a, b) => b.last - a.last);
    const gpuSeries = sorted.map((metric, index) => ({
      pod: getSeriesName(metric, (displayNameCounts.get(metric.displayName) ?? 0) > 1),
      data: metric.data.map((point) => ({ ...point, value: convertMibToGb(point.value) })),
      color: POD_SERIES_COLORS[index % POD_SERIES_COLORS.length],
    }));

    return {
      currentUsage,
      gpuInstanceCount: metrics.length,
      gpuSeries,
    };
  }, [gpuMemoryData]);

  const { currentUsage, gpuInstanceCount, gpuSeries } = gpuMemoryViewModel;
  const hasGpuMemoryData = gpuInstanceCount > 0;

  return (
    <div className={styles.container}>
      <MetricCard
        title="GPU"
        value={currentUsage}
        unit="GB"
        isLoading={isLoading}
        secondaryText={`GB · ${gpuInstanceCount} instances`}
        podHistory={gpuSeries}
        onDetailClick={hasGpuMemoryData ? handleDetailClick : undefined}
      />

      {hasGpuMemoryData && (
        <PodMetricDetailModal
          open={gpuMemoryModalOpen}
          onClose={handleDetailClick}
          title="GPU"
          unit="GB"
          podHistory={gpuSeries}
        />
      )}
    </div>
  );
};

const DefaultWorkerContent = () => {
  const { data, isLoading } = useGpuMemoryUsed();

  return <GpuMemoryMetricView data={data} isLoading={isLoading} />;
};

const Content = ({ data, isLoading }: GpuMemoryMetricContentProps) => {
  if (data) {
    return <GpuMemoryMetricView data={data} isLoading={isLoading} />;
  }

  return <DefaultWorkerContent />;
};

const Error = ({ title, description, ...props }: WidgetErrorProps) => {
  return (
    <div className={styles.container}>
      <ErrorFallback title={title} description={description} className={styles.error} {...props} />
    </div>
  );
};

export const GpuMemoryMetric = { Content, Error };
